import type { BillingPeriod } from '@squaredup/utilities';
import type { GroupCountResult, MetricsResults } from 'dynamo-wrapper';
import API from './API';
import { handleError, handleResponse } from './util';

export const ALL_DATA_SOURCES_OBJECTS_COUNT_KEY = ['ALL_DATA_SOURCES_OBJECTS', 'COUNT'];
export const PER_DATA_SOURCE_OBJECTS_COUNT_KEY = ['PER_DATA_SOURCE_OBJECTS', 'COUNT'];

/**
 * Get the number of users for the current tenants
 */
export const UserCount = async (): Promise<GroupCountResult[]> =>
    API.post<GroupCountResult[]>('/usage/counts/users').then(handleResponse).catch(handleError);

/**
 * Get the number of indexed nodes in the current tenant
 */
export const NodeCount = async (): Promise<GroupCountResult[]> =>
    API.post<GroupCountResult[]>('/usage/counts/nodes').then(handleResponse).catch(handleError);

/**
 * Get the number of indexed nodes in the current tenant by data source.
 * Optionally specify the data source configIds of interest, if omitted
 * then results are returned for all data source configIds.
 */
export const DataSourceNodeCount = async (configIds?: string[]): Promise<GroupCountResult[]> =>
    API.post<GroupCountResult[]>('/usage/counts/source-nodes', {
        configIds
    })
        .then(handleResponse)
        .catch(handleError);

export const MonitorCount = async () =>
    API.post<GroupCountResult[]>('/usage/counts/monitors').then(handleResponse).catch(handleError);

/**
 * Get the current number of monitors tenant wide, without access control
 */
export const GetCountOfMonitors = async () => {
    const result = await API.get<{ count: number }>('/usage/current/monitors').then(handleResponse).catch(handleError);
    return result.count;
};

/**
 * Get the current number of active shares tenant wide, without access control
 */
export const GetCountOfActiveShares = async () => {
    const result = await API.get<{ count: number }>('/usage/current/shares').then(handleResponse).catch(handleError);
    return result.count;
};

export const WorkspacesCount = async () =>
    API.post<GroupCountResult[]>('/usage/counts/workspaces').then(handleResponse).catch(handleError);

export const DashboardsCount = async () =>
    API.post<GroupCountResult[]>('/usage/counts/dashboards').then(handleResponse).catch(handleError);

/**
 * Get the number of AI tokens used for the current month.
 * Returns the sum of tokens (input + output) for all AI assistant requests.
 */
export const AiTokenCount = async (numberOfMonths = 1): Promise<GroupCountResult[]> =>
    API.post<GroupCountResult[]>('/usage/counts/aitokens', { numberOfMonths }).then(handleResponse).catch(handleError);

/**
 * Get the daily request counts, broken down by purpose, for the given billing period.
 */
export const DetailedRequestsForBillingPeriod = async (period: BillingPeriod): Promise<MetricsResults[]> =>
    API.post('/usage/counts/detailedrequests', {
        timeframe: {
            start: period.start.toISOString(),
            end: period.end.toISOString()
        },
        dataPoints: period.dayCount
    })
        .then(handleResponse)
        .catch(handleError);
